import React, { useState, useRef, useEffect } from "react";
import { DatePicker } from "rsuite";
import { DateTimeTypeEnum } from "../../../../types";
import {
  normalizeDateByType,
  parseInputFieldValue,
} from "../../utils/dateTimeUtils";

interface DatePickerRef {
  getValue?: () => Date | null;
  state?: {
    value?: Date | null;
  };
  value?: Date | null;
  root?: {
    querySelector?: (selector: string) => HTMLElement | null;
  };
}

export default function DateTimeComponent({
  label,
  type,
  value,
  onChange,
}: {
  label: string;
  type: DateTimeTypeEnum;
  value: string;
  onChange: (val: string | undefined, displayAs?: string) => void;
}) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<DatePickerRef | null>(null);
  const pendingCalendarValue = useRef<Date | null>(null);
  const originalPopupState = useRef<{
    parent: Node | null;
    position: string;
  } | null>(null);

  const handleCalendarOpen = () => {
    setIsCalendarOpen(true);
    pendingCalendarValue.current = null;
  };

  const handleCalendarClose = () => {
    // First, try to parse input field value (handles partial dates/times like "MM/25", "hh:01")
    let dateToSave: Date | null = null;

    if (datePickerRef.current) {
      const inputElement = datePickerRef.current?.root?.querySelector(
        "input",
      ) as HTMLInputElement;
      if (inputElement?.value) {
        const parsed = parseInputFieldValue(inputElement.value, type);
        if (parsed.isValid && parsed.value) {
          dateToSave = parsed.value;
        }
      }
    }

    // If no input value, use calendar selection
    if (!dateToSave && pendingCalendarValue.current) {
      const calendarValue = pendingCalendarValue.current;
      if (!isNaN(calendarValue.getTime())) {
        dateToSave = normalizeDateByType(new Date(calendarValue), type);
      }
    }

    // Save if we have a valid date
    if (dateToSave && !isNaN(dateToSave.getTime())) {
      const pendingTime = dateToSave.getTime();
      const savedTime = value ? new Date(value).getTime() : null;

      if (pendingTime !== savedTime) {
        onChangeVariable(dateToSave);
      }
    }

    // Cleanup
    restorePopupToOriginalPosition();
    setIsCalendarOpen(false);
    originalPopupState.current = null;
    pendingCalendarValue.current = null;
  };

  const handleCalendarDateChange = (date: Date) => {
    // Track selections in the calendar (even if not confirmed)
    pendingCalendarValue.current = date;
  };

  const restorePopupToOriginalPosition = () => {
    // Scope query to this component instance
    const popup = containerRef.current?.querySelector(
      ".rs-picker-popup",
    ) as HTMLElement;
    if (popup && originalPopupState.current) {
      const { parent, position } = originalPopupState.current;

      popup.style.position = position;

      if (parent && parent !== popup.parentNode) {
        parent.appendChild(popup);
      }

      originalPopupState.current = null;
    }
  };

  useEffect(() => {
    if (isCalendarOpen && containerRef.current) {
      const movePopupToContainer = () => {
        // Scope query to this component instance
        const popup = containerRef.current?.querySelector(
          ".rs-picker-popup",
        ) as HTMLElement;
        if (popup && containerRef.current) {
          originalPopupState.current = {
            parent: popup.parentNode,
            position: popup.style.position || "absolute",
          };

          popup.style.position = "static";

          if (popup.parentNode !== containerRef.current) {
            containerRef.current.appendChild(popup);
          }
        }
      };

      movePopupToContainer();
      const timeoutId = setTimeout(movePopupToContainer, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isCalendarOpen]);

  const onChangeVariable = (val: Date | null) => {
    if (!val || isNaN(val.getTime())) {
      onChange(undefined);
      return;
    }

    val = normalizeDateByType(val, type);
    if (isNaN(val.getTime())) return;

    val.setSeconds(0);
    let displayAs = "";
    if (type === DateTimeTypeEnum.DATE_TIME) {
      displayAs = val.toDateString() + " " + val.toLocaleTimeString();
    } else if (type === DateTimeTypeEnum.DATE) {
      displayAs = val.toDateString();
    } else if (type === DateTimeTypeEnum.TIME) {
      displayAs = val.toLocaleTimeString();
    }

    onChange(val.toISOString(), displayAs);
  };

  const renderDatePicker = () => {
    switch (type) {
      case DateTimeTypeEnum.DATE:
        return (
          <DatePicker
            ref={datePickerRef as React.Ref<HTMLDivElement>}
            container={containerRef.current}
            format="MM/dd/yyyy"
            value={value ? new Date(value) : null}
            onChange={onChangeVariable}
            onChangeCalendarDate={handleCalendarDateChange}
            onOpen={handleCalendarOpen}
            onClose={handleCalendarClose}
          />
        );
      case DateTimeTypeEnum.TIME:
        return (
          <DatePicker
            ref={datePickerRef as React.Ref<HTMLDivElement>}
            container={containerRef.current}
            format="hh:mm aa"
            value={value ? new Date(value) : null}
            onChange={onChangeVariable}
            onChangeCalendarDate={handleCalendarDateChange}
            showMeridian
            calendarDefaultDate={new Date()}
            placeholder="Select time"
            onOpen={handleCalendarOpen}
            onClose={handleCalendarClose}
          />
        );
      case DateTimeTypeEnum.DATE_TIME:
        return (
          <DatePicker
            ref={datePickerRef as React.Ref<HTMLDivElement>}
            container={containerRef.current}
            format="MM/dd/yyyy hh:mm aa"
            value={value ? new Date(value) : null}
            onChange={onChangeVariable}
            onChangeCalendarDate={handleCalendarDateChange}
            showMeridian
            onOpen={handleCalendarOpen}
            onClose={handleCalendarClose}
          />
        );
      default: {
        const _exhaustiveCheck: never = type;
        throw new Error(`Unhandled component type: ${_exhaustiveCheck}`);
      }
    }
  };

  return (
    <div className="space-y-2 px-4">
      <div
        className="text-md font-semibold"
        style={{
          wordBreak: "break-word",
          whiteSpace: "normal",
        }}
      >
        {label}
      </div>
      <div>
        {renderDatePicker()}
        {/* Container for the moved popup */}
        <div
          ref={containerRef}
          className="flex flex-col items-start mt-2"
          style={{
            minHeight: isCalendarOpen ? "200px" : "0",
            width: "100%",
            display: isCalendarOpen ? "flex" : "none",
          }}
        />
      </div>
    </div>
  );
}
